<template>
  <div v-if="warning" data-testid="warning-banner">
    <v-alert type="warning" outlined role="alert" data-testid="warning-text">
      {{ $t(textKey) }}
      <p v-if="warning.resolution === 'drawn'" class="mt-2 mb-0" data-testid="warning-drawn">{{ $t('damage_control.warnings.drawn') }}</p>
      <p v-if="warning.resolution === 'dismissed'" class="mt-2 mb-0" data-testid="warning-dismissed">
        {{ $t('damage_control.warnings.dismissed') }}<span v-if="warning.reason">: {{ warning.reason }}</span>
      </p>
      <div v-if="editable && !warning.resolution" class="dc-warning-actions">
        <v-btn outlined data-testid="warning-draw" :disabled="busy || disabled || refreshRequired || !canDraw" @click="$emit('draw')">{{ $t('damage_control.review.draw') }}</v-btn>
        <v-btn outlined data-testid="warning-dismiss" :disabled="busy || disabled || refreshRequired" @click="openDialog">{{ $t('damage_control.warnings.dismiss') }}</v-btn>
      </div>
    </v-alert>
    <p v-if="errorText" role="alert" data-testid="warning-error">{{ errorText }}</p>
    <v-btn v-if="refreshRequired" outlined data-testid="warning-refresh" :loading="busy" @click="refresh">{{ $t('damage_control.capture.refresh') }}</v-btn>
    <v-dialog v-model="dialog" max-width="480" :persistent="busy" data-testid="warning-dialog">
      <v-card>
        <v-card-title>{{ $t('damage_control.warnings.dismiss') }}</v-card-title>
        <v-card-text>
          <v-textarea v-model="reason" :disabled="busy" :label="$t('damage_control.warnings.reason')" :counter="reasonMax" :maxlength="reasonMax" data-testid="warning-reason" />
        </v-card-text>
        <v-card-actions class="dc-warning-actions">
          <v-btn text :disabled="busy" data-testid="warning-cancel" @click="dialog = false">{{ $t('damage_control.review.cancel') }}</v-btn>
          <v-btn color="primary" :loading="busy" :disabled="!editable || disabled" data-testid="warning-submit" @click="dismiss">{{ $t('damage_control.warnings.dismiss') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { damageControlApi, reportDamageControlFailure } from "@/api/damageControl";
import { shotWarning, warningTextKey } from "@/utils/damageControlWarnings";

import { shotStillCurrent } from "@/utils/damageControlReview";
import reviewRefreshMixin from "@/components/dashboard/damage_control/review/reviewRefreshMixin";

export default {
  name: "WarningBanner",
  mixins: [reviewRefreshMixin],
  props: {
    shot: { type: Object, required: true },
    inspection: { type: Object, required: true },
    zoneKey: { type: String, required: true },
    canDraw: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },
  // Предел причины — как в API (Warnings::Dismiss: не длиннее 500 символов).
  data: () => ({ reasonMax: 500, dialog: false, reason: "" }),
  computed: {
    // Форма ответа API — только в shotWarning / dismissWarning.
    warning() { return shotWarning(this.shot); },
    textKey() { return warningTextKey(this.warning, this.inspection.side); },
    editable() {
      return this.inspection.status === "in_progress" && this.shot.current !== false && !this.shot.superseded_by_id &&
        shotStillCurrent(this.inspection, this.zoneKey, this.shot);
    },
  },
  watch: {
    editable(value) { if (!value) this.dialog = false; },
  },
  methods: {
    openDialog() { this.reason = ""; this.dialog = true; },
    async dismiss() {
      if (!this.editable || this.disabled || this.busy || this.refreshRequired || !this.warning || this.warning.resolution) return;
      this.busy = true;
      this.errorText = "";
      try {
        await damageControlApi.dismissWarning(this.axios, this.warning.id, this.reason.trim().slice(0, this.reasonMax) || undefined);
        this.dialog = false;
        this.refreshRequired = true;
        await this.reload();
      } catch (error) { this.errorText = reportDamageControlFailure(this, error, { silent: true }); }
      finally { this.busy = false; }
    },
  },
};
</script>

<style scoped>
.dc-warning-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 12px; }
.dc-warning-actions .v-btn { white-space: normal; height: auto; min-height: 40px; padding: 8px 16px; }
@media (max-width: 599px) { .v-btn { width: 100%; margin-left: 0; } }
</style>
